import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AuthContext } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { resetPassword } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Please enter your email');
      return;
    }

    setLoading(true);
    const result = await resetPassword(email);
    setLoading(false);

    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error || 'Something went wrong');
    }
  };

  return (
    <div className="mobile-container bg-white flex flex-col">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="flex-1 flex flex-col px-6 pt-12 pb-8"
      >
        <button
          onClick={() => navigate('/login')}
          className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center mb-8"
        >
          <SafeIcon name="ArrowLeft" className="text-gray-600" />
        </button>

        <div className="mb-8">
          <img
            src="https://cloud.sharemyimage.com/2026/08/16/App-Icon.png"
            alt="DayClap"
            className="w-36 h-36 rounded-3xl mb-5 shadow-sm"
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Reset password</h1>
          <p className="text-gray-500">
            {success
              ? "Check your email for a reset link."
              : "Enter your email and we'll send you a link to reset your password."}
          </p>
        </div>

        {success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-green-50 border border-green-100 p-6 rounded-2xl text-center"
          >
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon name="Mail" className="text-3xl text-green-600" />
            </div>
            <h3 className="font-bold text-green-800 mb-2">Email sent! 📧</h3>
            <p className="text-sm text-green-600 mb-4">
              We sent a password reset link to <strong>{email}</strong>. Check your inbox and follow the instructions.
            </p>
            <div className="bg-white rounded-xl p-4 border border-green-100 text-left">
              <p className="text-xs text-gray-500 font-semibold mb-2">What happens next:</p>
              <ol className="text-xs text-gray-500 space-y-1.5 list-decimal list-inside">
                <li>Click the "Reset Password" button in the email</li>
                <li>Enter your new password twice</li>
                <li>Click "Update Password" to confirm</li>
                <li>Sign in with your new password</li>
              </ol>
            </div>
            <p className="text-xs text-green-500 mt-4">
              Didn't receive it? Check your spam folder or try again.
            </p>
            <button
              onClick={() => { setSuccess(false); setEmail(''); }}
              className="mt-3 text-sm text-green-700 font-semibold underline"
            >
              Try a different email
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
              <div className="relative">
                <SafeIcon name="Mail" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none transition-all"
                />
              </div>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="bg-red-50 border border-red-100 text-red-600 text-sm p-3 rounded-xl flex items-center gap-2"
              >
                <SafeIcon name="AlertCircle" />
                {error}
              </motion.div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-pink-200 hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>
        )}

        <p className="text-center text-sm text-gray-500 mt-8">
          Remember your password?{' '}
          <Link to="/login" className="text-pink-500 font-semibold">
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;